import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2, RotateCcw, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import LoadingSpinner from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Lixeira = () => {
  const [receitas, setReceitas] = useState([]);
  const [loading, setLoading] = useState(true);

  const carregarLixeira = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/receitas/lixeira`);
      setReceitas(res.data.receitas || []);
    } catch {
      toast.error("Erro ao carregar lixeira");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarLixeira();
  }, []);

  const restaurarReceita = async (id) => {
    try {
      await axios.post(`${API}/receitas/lixeira/${id}/restaurar`);
      toast.success("Receita restaurada com sucesso!");
      carregarLixeira();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao restaurar receita");
    }
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  return (
    <div className="space-y-6" data-testid="lixeira-container">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Trash2 className="w-8 h-8 text-slate-600" />
            Lixeira
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Receitas excluídas ficam aqui por 30 dias antes da exclusão permanente
          </p>
        </div>
      </div>

      {/* Aviso */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-amber-800">
            As receitas são excluídas automaticamente após 30 dias
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Não é possível excluir manualmente receitas da lixeira. Use o botão "Restaurar" para recuperar uma receita.
          </p>
        </div>
      </div>

      {/* Lista de receitas na lixeira */}
      <Card className="bg-white border border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-lg font-semibold text-slate-800">
            Receitas na Lixeira ({receitas.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-12">
              <LoadingSpinner message="Carregando lixeira..." />
            </div>
          ) : receitas.length === 0 ? (
            <div className="py-12 text-center">
              <Trash2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">A lixeira está vazia</p>
              <p className="text-xs text-slate-400 mt-1">
                Receitas excluídas aparecerão aqui
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {receitas.map((receita) => (
                <div
                  key={receita.id}
                  className="p-4 hover:bg-slate-50 transition-colors"
                  data-testid={`lixeira-item-${receita.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-900">
                          {receita.nome_cliente || "Sem nome"}
                        </span>
                        {receita.os && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                            OS: {receita.os}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500">
                        {receita.cpf_cliente && (
                          <span>CPF: {receita.cpf_cliente}</span>
                        )}
                        {receita.telefone_cliente && (
                          <span>Tel: {receita.telefone_cliente}</span>
                        )}
                        {receita.nome_loja && (
                          <span>Loja: {receita.nome_loja}</span>
                        )}
                        <span>Valor: {fmt(receita.valor_total)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-4 h-4 text-amber-500" />
                        <span className={`text-sm font-medium ${
                          receita.dias_restantes <= 7 
                            ? "text-red-600" 
                            : receita.dias_restantes <= 15 
                            ? "text-amber-600" 
                            : "text-slate-600"
                        }`}>
                          {receita.dias_restantes} dias restantes para exclusão
                        </span>
                      </div>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restaurarReceita(receita.id)}
                      className="flex-shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      data-testid={`btn-restaurar-${receita.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restaurar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Lixeira;
